import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/authStore';
import {
  LayoutDashboard,
  Compass,
  Download,
  Heart,
  BookOpen,
  User,
  Settings,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Receipt,
  ShieldCheck,
  Languages,
  Users,
  Inbox,
  FolderKanban,
  FileText,
  Activity,
  MessageSquare,
  Handshake,
  Building2,
  DollarSign
} from 'lucide-react';
import { MobileTopBar } from './MobileTopBar';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileDrawer } from './MobileDrawer';
import { AbnyWebBadge } from '../common/AbnyWebBadge';

export const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const sidebarSections = [
    {
      title: 'OPERATIONS',
      items: [
        { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, exact: true },
        { name: 'Publications Catalog', href: '/admin/cms/publications', icon: BookOpen },
        { name: 'Publishing Projects', href: '/admin/projects', icon: FolderKanban },
        { name: 'Documents Repository', href: '/admin/documents', icon: FileText },
        { name: 'Client Messages', href: '/admin/messages', icon: MessageSquare },
      ],
    },
    {
      title: 'PARTNER PUBLISHERS',
      items: [
        { name: 'Publishers & Rights CRM', href: '/admin/partners', icon: Handshake },
      ],
    },
    {
      title: 'CLIENTS & TRANSLATION CRM',
      items: [
        { name: 'Translation Clients CRM', href: '/admin/customers', icon: Users },
        { name: 'Vernacular Translations', href: '/admin/translations', icon: Languages },
      ],
    },
    {
      title: 'FINANCE & REVENUE',
      items: [
        { name: 'Revenue & Invoices', href: '/admin/revenue', icon: Receipt },
      ],
    },
    {
      title: 'INTERNAL STAFFING',
      items: [
        { name: 'Staff & Roles Directory', href: '/admin/staff', icon: ShieldCheck },
      ],
    },
  ];

  const secondaryMenuItems = [
    { name: 'Settings & RFQs', href: '/admin/enquiries', icon: Settings },
    { name: 'Audit Trail Logs', href: '/admin/activity-logs', icon: Activity },
    { name: 'Bookstore (forthetruth.in)', href: 'https://forthetruth.in', icon: ExternalLink, external: true },
  ];

  const isActive = (item: { href: string; exact?: boolean }) => {
    if (item.exact) return location.pathname === item.href;
    return location.pathname.startsWith(item.href);
  };

  const getPageTitle = () => {
    if (location.pathname.startsWith('/admin/enquiries')) return 'ENQUIRIES & SETTINGS';
    if (location.pathname.startsWith('/admin/partners')) return 'PARTNER PUBLISHERS CRM';
    if (location.pathname.startsWith('/admin/customers')) return 'CLIENTS & TRANSLATION CRM';
    if (location.pathname.startsWith('/admin/translations')) return 'VERNACULAR TRANSLATIONS PIPELINE';
    if (location.pathname.startsWith('/admin/revenue')) return 'FINANCE & REVENUE CENTER';
    if (location.pathname.startsWith('/admin/staff')) return 'INTERNAL STAFFING & ROLES';
    if (location.pathname.startsWith('/admin/projects')) return 'PROJECT TIMELINES';
    if (location.pathname.startsWith('/admin/documents')) return 'GLOBAL REPOSITORY';
    if (location.pathname.startsWith('/admin/messages')) return 'COMMUNICATIONS HUB';
    if (location.pathname.startsWith('/admin/cms/publications')) return 'PUBLICATIONS CATALOG';
    if (location.pathname.startsWith('/admin/activity-logs')) return 'SYSTEM AUDIT TRAIL';
    return 'FOR THE TRUTH CONTROL CENTER';
  };

  return (
    <div className="min-h-screen bg-[#EEF2F6] text-slate-900 flex flex-col md:flex-row antialiased font-sans p-0 md:p-4 lg:p-6 gap-6">
      
      {/* Android Mobile Top Bar */}
      <MobileTopBar
        title={getPageTitle()}
        onOpenDrawer={() => setDrawerOpen(true)}
        theme="light"
      />

      {/* Desktop Floating Rounded-3xl White Sidebar */}
      <aside
        className={`hidden md:flex flex-col justify-between bg-white rounded-3xl border border-slate-200/90 shadow-sm transition-all duration-300 shrink-0 sticky top-6 h-[calc(100vh-3rem)] overflow-hidden ${
          collapsed ? 'w-20 p-3' : 'w-64 lg:w-72 p-5'
        }`}
      >
        <div className="overflow-y-auto overflow-x-hidden space-y-6 pr-1">
          
          {/* Brand Header */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-3 overflow-hidden">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#FF6B4A] to-[#EA580C] text-white flex items-center justify-center shrink-0 shadow-md">
                <BookOpen className="w-5 h-5" />
              </div>
              {!collapsed && (
                <div className="flex flex-col truncate">
                  <span className="font-extrabold text-sm text-slate-900 tracking-tight truncate">
                    FOR THE TRUTH PRESS
                  </span>
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider truncate">
                    Private Limited &bull; Control
                  </span>
                </div>
              )}
            </Link>
          </div>

          {/* Divided Nav Sections */}
          <div className="space-y-5">
            {sidebarSections.map((section) => (
              <div key={section.title} className="space-y-1.5">
                {!collapsed && (
                  <h4 className="px-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">
                    {section.title}
                  </h4>
                )}

                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item);

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        title={collapsed ? item.name : undefined}
                        className={`flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-xs font-bold transition-all ${
                          active
                            ? 'bg-[#FF6B4A] text-white shadow-md shadow-orange-500/25'
                            : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100/80'
                        }`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-slate-500'}`} />
                        {!collapsed && <span className="truncate">{item.name}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* Sidebar Bottom Section: Settings, Bookstore, Log Out, ABNY Web Pill */}
        <div className="pt-4 border-t border-slate-100 space-y-1.5 shrink-0 bg-white">
          
          {secondaryMenuItems.map((item) => {
            const Icon = item.icon;
            if (item.external) {
              return (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={collapsed ? item.name : undefined}
                  className="flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:text-slate-950 hover:bg-slate-100/80 transition-colors"
                >
                  <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </a>
              );
            }
            return (
              <Link
                key={item.name}
                to={item.href}
                title={collapsed ? item.name : undefined}
                className="flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:text-slate-950 hover:bg-slate-100/80 transition-colors"
              >
                <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                {!collapsed && <span className="truncate">{item.name}</span>}
              </Link>
            );
          })}

          <button
            onClick={logout}
            title={collapsed ? 'Log Out' : undefined}
            className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-50 transition-colors cursor-pointer"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Sign Out</span>}
          </button>

          {/* Powered by ABNY Web Badge */}
          {!collapsed ? (
            <div className="pt-2 flex justify-center">
              <AbnyWebBadge size="sm" className="w-full justify-between" />
            </div>
          ) : null}

          {/* Collapse Toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 text-xs transition-colors mt-1"
            title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

      </aside>

      {/* Main Viewport on #EEF2F6 Canvas */}
      <main className="flex-1 flex flex-col min-w-0 pb-20 md:pb-0 overflow-y-auto">
        <Outlet />
      </main>

      {/* Android Mobile Navigation & Bottom Drawer */}
      <MobileBottomNav onOpenDrawer={() => setDrawerOpen(true)} />
      <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} theme="light" />

    </div>
  );
};
